import React from "react";
import { recruiters } from "../assets/data/recruiters";

export default function Placement({ course }) {
  const support = [
    {
      title: "Career Guidance",
      short: "Direction",
      description:
        "Explore career directions aligned with your programme, strengths and professional aspirations.",
    },
    {
      title: "Employability Skills",
      short: "Develop",
      description:
        "Strengthen communication, aptitude, professional etiquette and workplace-oriented capabilities.",
    },
    {
      title: "Profile Preparation",
      short: "Present",
      description:
        "Build a professional resume and learn to communicate your academic projects, skills and achievements.",
    },
    {
      title: "Recruitment Preparation",
      short: "Prepare",
      description:
        "Develop greater confidence for interviews and other stages of the recruitment process.",
    },
  ];

  return (
    <section id="placements" className="relative overflow-hidden bg-[#071A2F] py-20 text-white sm:py-24 lg:py-28">
      {/* ======================================================
          ABSTRACT BACKGROUND
      ====================================================== */}

      <div
        aria-hidden="true"
        className="pointer-events-none absolute left-[-180px] top-[80px] h-[500px] w-[500px] rounded-full border border-white/[0.04]"
      />

      <div
        aria-hidden="true"
        className="pointer-events-none absolute left-[-100px] top-[160px] h-[340px] w-[340px] rounded-full border border-[#D5AD57]/[0.07]"
      />

      <div
        aria-hidden="true"
        className="pointer-events-none absolute bottom-0 right-0 h-[320px] w-[320px] border-l border-t border-white/[0.04]"
      />

      <div className="relative mx-auto w-[min(1380px,calc(100%-48px))] max-sm:w-[calc(100%-28px)]">

        {/* ======================================================
            INTRO
        ====================================================== */}

        <div className="grid gap-12 lg:grid-cols-[1.05fr_.95fr] lg:items-end lg:gap-20">

          <div>
            <div className="flex items-center gap-4">
              <span className="h-[2px] w-12 bg-[#D5AD57]" />

              <span className="text-sm font-bold uppercase tracking-[0.18em] text-[#D5AD57]">
                Placement Assistance
              </span>
            </div>

            <h2 className="mt-7 max-w-[800px] font-['Libre_Caslon_Display',Georgia,serif] text-[clamp(46px,5.4vw,76px)] font-normal leading-[1.01] tracking-[-0.045em]">
              Ready for more than
              <span className="block text-[#D5AD57]">
                your first interview.
              </span>
            </h2>
          </div>

          <div className="lg:pb-2">
            <p className="max-w-[520px] text-base leading-8 text-white/60">
              The University&apos;s career support ecosystem helps students
              translate academic learning into professional readiness through
              guidance, skill development and recruitment preparation.
            </p>

            <a
              href="/placements"
              className="group mt-7 inline-flex items-center gap-5 text-sm font-bold uppercase tracking-[0.12em] text-white"
            >
              <span className="border-b border-white/30 pb-1 transition-colors group-hover:border-[#D5AD57] group-hover:text-[#D5AD57]">
                Visit Placement Cell
              </span>

              <span className="text-[#D5AD57] transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1">
                ↗
              </span>
            </a>
          </div>
        </div>

        {/* ======================================================
            CAREER LAUNCH CENTRE
        ====================================================== */}

        <div className="relative mt-16 border-y border-white/15 py-12 lg:mt-20 lg:py-16">

          <div className="grid gap-12 lg:grid-cols-[1fr_330px_1fr] lg:items-center xl:grid-cols-[1fr_380px_1fr]">

            {/* ==================================================
                LEFT CAPABILITIES
            ================================================== */}

            <div className="space-y-12 lg:space-y-20">

              {support.slice(0, 2).map((item, index) => (
                <article
                  key={item.title}
                  className={`group ${index === 1 ? "lg:ml-16" : ""
                    }`}
                >
                  <div className="flex gap-5">
                    <span className="mt-2 h-2.5 w-2.5 shrink-0 rotate-45 bg-[#D5AD57]" />

                    <div>
                      <span className="text-[11px] font-bold uppercase tracking-[0.17em] text-[#D5AD57]">
                        {item.short}
                      </span>

                      <h3 className="mt-2 font-['Libre_Caslon_Display',Georgia,serif] text-[29px] font-normal leading-tight">
                        {item.title}
                      </h3>

                      <p className="mt-3 max-w-[360px] text-sm leading-7 text-white/50">
                        {item.description}
                      </p>
                    </div>
                  </div>
                </article>
              ))}
            </div>

            {/* ==================================================
                CENTRE VISUAL
            ================================================== */}

            <div className="relative mx-auto grid h-[310px] w-[310px] place-items-center sm:h-[350px] sm:w-[350px] lg:h-[330px] lg:w-[330px] xl:h-[380px] xl:w-[380px]">

              {/* rings */}

              <span className="absolute inset-0 rounded-full border border-white/15" />

              <span className="absolute inset-[24px] rounded-full border border-dashed border-[#D5AD57]/30" />

              <span className="absolute inset-[54px] rounded-full border border-white/10" />

              {/* orbital nodes */}

              <span className="absolute left-1/2 top-[-5px] h-3 w-3 -translate-x-1/2 rounded-full bg-[#D5AD57]" />

              <span className="absolute bottom-[20%] right-[3%] h-2 w-2 rounded-full bg-white/60" />

              <span className="absolute left-[4%] top-[35%] h-2 w-2 rounded-full bg-white/30" />

              {/* centre */}

              <div className="relative z-[2] grid h-[190px] w-[190px] place-items-center rounded-full bg-[#D5AD57] text-center text-[#071A2F] sm:h-[210px] sm:w-[210px]">
                <div className="px-5">
                  <span className="text-[11px] font-bold uppercase tracking-[0.18em]">
                    Career Ready
                  </span>

                  <strong className="mt-3 block font-['Libre_Caslon_Display',Georgia,serif] text-[42px] font-normal leading-[0.95] sm:text-[48px]">
                    Learn
                    <br />
                    to Lead
                  </strong>

                  <span className="mt-4 block text-xl">↗</span>
                </div>
              </div>
            </div>

            {/* ==================================================
                RIGHT CAPABILITIES
            ================================================== */}

            <div className="space-y-12 lg:space-y-20">

              {support.slice(2, 4).map((item, index) => (
                <article
                  key={item.title}
                  className={`group ${index === 0 ? "lg:ml-16" : ""
                    }`}
                >
                  <div className="flex gap-5">
                    <span className="mt-2 h-2.5 w-2.5 shrink-0 rotate-45 bg-[#D5AD57]" />

                    <div>
                      <span className="text-[11px] font-bold uppercase tracking-[0.17em] text-[#D5AD57]">
                        {item.short}
                      </span>

                      <h3 className="mt-2 font-['Libre_Caslon_Display',Georgia,serif] text-[29px] font-normal leading-tight">
                        {item.title}
                      </h3>

                      <p className="mt-3 max-w-[360px] text-sm leading-7 text-white/50">
                        {item.description}
                      </p>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </div>

        {/* ======================================================
            VALUE STATEMENT
        ====================================================== */}

        <div className="grid border-b border-white/15 lg:grid-cols-[270px_1fr_auto] lg:items-center">

          <div className="border-white/15 py-8 lg:border-r lg:pr-8">
            <span className="text-xs font-bold uppercase tracking-[0.17em] text-[#D5AD57]">
              The Objective
            </span>
          </div>

          <div className="py-8 lg:px-10">
            <p className="max-w-[760px] font-['Libre_Caslon_Display',Georgia,serif] text-[24px] font-normal leading-[1.4] text-white/90 sm:text-[28px]">
              Helping students move from{" "}
              <span className="text-white/45">academic potential</span> to{" "}
              <span className="text-[#D5AD57]">
                professional confidence.
              </span>
            </p>
          </div>

          <div className="hidden py-8 lg:block">
            <span className="text-[38px] text-[#D5AD57]">→</span>
          </div>
        </div>

        {/* ======================================================
            RECRUITER SECTION
        ====================================================== */}

        <div className="mt-16 lg:mt-20">

          <div className="flex flex-col gap-6 sm:flex-row sm:items-end sm:justify-between">

            <div>
              <span className="text-xs font-bold uppercase tracking-[0.17em] text-[#D5AD57]">
                Industry Exposure
              </span>

              <h3 className="mt-4 font-['Libre_Caslon_Display',Georgia,serif] text-[34px] font-normal sm:text-[42px]">
                Recruiter ecosystem
              </h3>
            </div>

            <p className="max-w-[500px] text-sm leading-7 text-white/45">
              Recruitment participation varies by programme, eligibility,
              vacancies and individual employer requirements.
            </p>
          </div>

          {/* ====================================================
              RECRUITER RIBBON
          ==================================================== */}

          {recruiters.length > 0 ? (
            <div className="mt-9 flex flex-wrap border-y border-white/15">

              {recruiters.map((recruiter) => (
                <div
                  key={recruiter}
                  className="group flex min-h-[110px] min-w-[160px] flex-1 items-center justify-center border border-white/15 px-7 transition-colors hover:bg-white/[0.04]"
                >
                  <img
                    src={recruiter}
                    alt={recruiter}
                    className="max-h-[90px] max-w-full object-contain bg-white p-1"
                  />
                </div>
              ))}
            </div>
          ) : (
            <div className="mt-9 flex min-h-[110px] items-center justify-between gap-8 border-y border-white/15">

              <span className="text-sm text-white/40">
                Verified recruiter logos can appear in this space.
              </span>

              <span className="hidden text-[#D5AD57] sm:block">———— ↗</span>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}